import {Component, computed, effect, Signal} from '@angular/core';
import {routingMetadata} from '../../signal/common/routing-matadata.signal';
import {Theme} from '../../enum/theme.enum';
import {ComponentMetadata} from '../../model/component-metadata.model';
import {landingSectionsSignal} from '../../signal/common/landing-sections.signal';
import {Button} from 'primeng/button';
import {NgIcon, provideIcons} from '@ng-icons/core';
import {tablerMenu2} from '@ng-icons/tabler-icons';
import {Drawer} from 'primeng/drawer';
import {NavigatorDirective} from '../../directive/navigator.directive';
import {RichResultsService} from '../../rich-results/rich-results.service';
import {buildBreadcrumbList, buildListItem} from '../../rich-results/rich-results.builders';
import {GoogleAnalyticsService} from '../../analytics/google/google-analytics.service';
import {AnalyticsEventCategory, AnalyticsEventName} from '../../enum/gtm-cookie-consent.enum';
import {PixelService} from '../../analytics/meta/pixel.service';
import {PixelEventName} from '../../analytics/meta/types/pixel.types';
import {MicrosoftClarityService} from '../../analytics/microsoft/microsoft-clarity.service';
import {MicrosoftClarityEvents} from '../../analytics/microsoft/types/microsoft-clarity-events.enum';
import {MicrosoftClaritySessionBumper} from '../../analytics/microsoft/types/microsoft-clarity-session-bumper.enum';
import {phoneBannerVisibilitySignal} from '../../signal/common/phone-banner-visibility.signal';
import {consentBannerVisibilitySignal} from '../../signal/common/consent-banner-visibility.signal';
import {PrimeTemplate} from 'primeng/api';

@Component({
  selector: 'app-navbar',
  imports: [
    Button,
    NgIcon,
    Drawer,
    NavigatorDirective,
    PrimeTemplate
  ],
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  providers: [provideIcons({tablerMenu2})]
})
export class NavbarComponent {
  protected logotypeUrl: Signal<string> = computed(() => routingMetadata()?.logotypeUrl || ' ');
  protected phone = computed(() => routingMetadata().contact?.phone[0] || undefined);
  private readonly theme: Signal<Theme> = computed(() => routingMetadata()?.theme || Theme.BASE);
  protected sections: Signal<ComponentMetadata[]> = computed<ComponentMetadata[]>(() => landingSectionsSignal());
  protected formSectionId: Signal<string | undefined> = computed(() =>
    this.sections().find(section => section.id === 'application-form')?.id
  );
  protected isMenuToggled = false;

  constructor(
    private readonly richResultsService: RichResultsService,
    private readonly googleAnalyticsService: GoogleAnalyticsService,
    private readonly pixelService: PixelService,
    private readonly microsoftClarityService: MicrosoftClarityService) {
    effect(() => {
      const sections = this.sections();
      const currentTheme = this.theme();

      if (sections.length) {
        const breadcrumbsSchema = buildBreadcrumbList({
          itemListElement: sections
            .filter(section => section.listable)
            .map((section, idx) =>
              buildListItem({
                position: idx + 1,
                item: {
                  id: section.id,
                  name: section.label,
                  url: `https://landing.processway.pl/${currentTheme}#${section.id}`
                }
              })
            )
        });

        this.richResultsService.register(breadcrumbsSchema);
      }
    });
  }

  protected onClose(): void {
    this.isMenuToggled = false;
  }

  protected trackApplicationCta(): void {
    this.googleAnalyticsService.trackEvent({
      event: AnalyticsEventName.ApplicationCtaClick,
      eventCategory: AnalyticsEventCategory.CTA,
      eventPayload: this.theme()
    });

    this.pixelService.trackEvent(
      PixelEventName.ApplicationCta, {
        page: this.theme()
      });

    this.microsoftClarityService.trackEvent(MicrosoftClarityEvents.RequestCta);
    this.microsoftClarityService.upgradeSession(MicrosoftClaritySessionBumper.RequestCtaClicked);
  }

  protected onPhoneButtonClick(): void {
    if (!consentBannerVisibilitySignal()) {
      phoneBannerVisibilitySignal.set(true);
    }

    this.microsoftClarityService.trackEvent(MicrosoftClarityEvents.PhoneClick);
    this.microsoftClarityService.upgradeSession(MicrosoftClaritySessionBumper.PhoneBanner);

    this.googleAnalyticsService.trackEvent({
        event: AnalyticsEventName.PhoneClick,
        eventCategory: AnalyticsEventCategory.CTA
      }
    );

    if (this.phone()) {
      this.pixelService.trackEvent(PixelEventName.PhoneClick, {
        phone: this.phone() as string
      });
    }
  }
}
